import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { useDispatch } from 'react-redux';

import ImageManagement from 'components/ImageManagement/ImageManagement';
import { updateSkuItemAttributesApi } from 'features/sku-management/sku-management.api';

const TITLE_MAX_LEN = 80;

const emptyFitmentRow = () => ({
  id: `fit-new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  Year: '',
  Make: '',
  Model: '',
  Trim: '',
  Engine: '',
  Notes: '',
});

const normalizeSpecRow = (sp, i) => ({
  id: `spec-${i}-${String(sp?.key ?? sp?.name ?? '').slice(0, 20)}`,
  key: String(sp?.key ?? sp?.name ?? ''),
  value: String(sp?.value ?? ''),
});

const EBAY_MAX_IMAGES = 24;

const catalogUrlsToImageObjects = (urls) =>
  (urls || []).map((url, index) => ({
    id: `catalog-img-${index}-${String(url).slice(-24)}`,
    url: String(url),
    isMain: index === 0,
    isValid: true,
    width: 1600,
    height: 1200,
    isNew: false,
  }));

const normalizeFitmentRow = (f, i) => ({
  id: `fit-${i}`,
  Year: f?.Year != null ? String(f.Year) : '',
  Make: f?.Make != null ? String(f.Make) : '',
  Model: f?.Model != null ? String(f.Model) : '',
  Trim: f?.Trim != null ? String(f.Trim) : '',
  Engine: f?.Engine != null ? String(f.Engine) : '',
  Notes: f?.Notes != null ? String(f.Notes) : f?.notes != null ? String(f.notes) : '',
});

const ItemsCatalogAttributeModals = ({
  modal,
  onClose,
  onAfterSave,
  borderColor,
  headerBg,
  mutedTextColor,
  inputColor,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftSpecs, setDraftSpecs] = useState([]);
  const [draftFitment, setDraftFitment] = useState([]);
  const [draftImages, setDraftImages] = useState([]);
  const [errorImages, setErrorImages] = useState([]);

  const row = modal?.row;
  const kind = modal?.kind;
  const draftKey = row && kind ? `${row.id}:${kind}` : '';

  useEffect(() => {
    if (!draftKey || !modal?.row || !modal?.kind) return;
    const r = modal.row;
    const k = modal.kind;
    if (k === 'title') {
      setDraftTitle(r.displayTitle || r.attributes?.catalogTitle || '');
    }
    if (k === 'specs') {
      setDraftSpecs((r.attributes?.specifications || []).map(normalizeSpecRow));
    }
    if (k === 'fitment') {
      const rows = r.attributes?.fitment || [];
      setDraftFitment(rows.length ? rows.map(normalizeFitmentRow) : []);
    }
    if (k === 'images') {
      setDraftImages(catalogUrlsToImageObjects(r.attributes?.images || []));
      setErrorImages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  const saveAttributes = async (body, successLabel) => {
    setSaving(true);
    try {
      const data = await dispatch(
        updateSkuItemAttributesApi({ skuMongoId: row.id, body }),
      ).unwrap();
      const results = data?.listingResults || [];
      const failed = results.filter((r) => !r.success).length;
      toast({
        title: successLabel,
        description:
          results.length === 0
            ? 'Catalog saved (no linked listings to update).'
            : failed > 0
              ? `Catalog saved; ${results.length - failed} of ${results.length} eBay listing(s) updated.`
              : `Catalog and ${results.length} linked eBay listing(s) updated.`,
        status: failed > 0 ? 'warning' : 'success',
        duration: 6000,
      });
      onClose();
      onAfterSave?.();
    } catch (e) {
      toast({
        title: 'Update failed',
        description:
          (typeof e === 'string' && e) ||
          e?.payload?.message ||
          e?.message ||
          'Could not update catalog or listings',
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTitle = () =>
    saveAttributes({ title: draftTitle.trim() }, 'Title updated');

  const handleSaveSpecs = () => {
    const specifications = draftSpecs
      .filter((r) => r.key.trim() !== '' || r.value.trim() !== '')
      .map((r) => ({ key: r.key.trim(), value: r.value.trim() }));
    return saveAttributes({ specifications }, 'Specifications updated');
  };

  const handleSaveFitment = () => {
    const fitment = draftFitment
      .filter(
        (r) =>
          r.Year.trim() ||
          r.Make.trim() ||
          r.Model.trim() ||
          r.Trim.trim() ||
          r.Engine.trim() ||
          r.Notes.trim(),
      )
      .map((r) => ({
        Year: r.Year.trim(),
        Make: r.Make.trim(),
        Model: r.Model.trim(),
        Trim: r.Trim.trim(),
        Engine: r.Engine.trim(),
        Notes: r.Notes.trim(),
      }));
    return saveAttributes({ fitment }, 'Fitment updated');
  };

  const handleSaveImages = () => {
    if (errorImages.length > 0) {
      toast({
        title: 'Fix image errors',
        description: 'Remove or replace images that do not meet marketplace size requirements.',
        status: 'error',
        duration: 5000,
      });
      return;
    }
    if (draftImages.some((img) => img.isValid === false)) {
      toast({
        title: 'Invalid images',
        description: 'Remove images marked with an error before saving.',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    const imagesForUpload = draftImages.map((img) => ({
      url: img.url,
      data: img.data,
      name: img.name,
      isNew: !!img.isNew,
      isUrl: !!img.isUrl,
    }));

    return saveAttributes({ imagesForUpload }, 'Images updated');
  };

  const addSpecRow = () => {
    setDraftSpecs((prev) => [...prev, { id: `spec-new-${Date.now()}`, key: '', value: '' }]);
  };

  const updateSpec = (id, field, value) => {
    setDraftSpecs((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const removeSpec = (id) => {
    setDraftSpecs((prev) => prev.filter((r) => r.id !== id));
  };

  const removeFitment = (id) => {
    setDraftFitment((prev) => prev.filter((r) => r.id !== id));
  };

  if (!row) return null;

  const getMaxImages = () => EBAY_MAX_IMAGES;

  const fitmentRowCount = draftFitment.filter(
    (r) =>
      r.Year.trim() ||
      r.Make.trim() ||
      r.Model.trim() ||
      r.Trim.trim() ||
      r.Engine.trim() ||
      r.Notes.trim(),
  ).length;

  return (
    <>
      <Modal isOpen={kind === 'title'} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr={14}>Edit catalog title — {row.canonicalSku}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" borderRadius="md" mb={4}>
              <AlertIcon />
              Saves to the catalog chunk and updates linked eBay listing titles (max {TITLE_MAX_LEN}{' '}
              characters).
            </Alert>
            <FormControl>
              <FormLabel>Title</FormLabel>
              <Input
                value={draftTitle}
                onChange={(e) => {
                  if (e.target.value.length <= TITLE_MAX_LEN) {
                    setDraftTitle(e.target.value);
                  }
                }}
                color={inputColor}
              />
              <FormHelperText>{TITLE_MAX_LEN - draftTitle.length} characters remaining</FormHelperText>
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveTitle} isLoading={saving}>
              Save &amp; update listings
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={kind === 'images'} onClose={onClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="min(1100px, 96vw)">
          <ModalHeader pr={14}>Edit catalog images — {row.canonicalSku}</ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4} px={4}>
            <VStack align="stretch" spacing={4}>
              <Alert status="info" borderRadius="md" py={3} px={4}>
                <AlertIcon mt={0.5} />
                <Text fontSize="sm" lineHeight="1.6">
                  Same editor as the listing page: drag to reorder, first image is main, up to{' '}
                  {EBAY_MAX_IMAGES} images. New files upload to eBay once, then catalog and all linked
                  listings are updated.
                </Text>
              </Alert>
              <Box overflowX="auto">
                <ImageManagement
                  title="Catalog images"
                  embedded
                  isAmazon={false}
                  images={draftImages}
                  setImages={setDraftImages}
                  errorImages={errorImages}
                  setErrorImages={setErrorImages}
                  getMaxImages={getMaxImages}
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveImages}
              isLoading={saving}
              isDisabled={errorImages.length > 0}
            >
              Save &amp; update listings
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={kind === 'specs'} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="56rem">
          <ModalHeader pr={14}>Edit catalog specs — {row.canonicalSku}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" borderRadius="md" mb={4}>
              <AlertIcon />
              Saves to the catalog chunk and pushes item specifics to linked eBay listings.
            </Alert>
            <HStack mb={3}>
              <Button size="sm" leftIcon={<AddIcon />} onClick={addSpecRow}>
                Add row
              </Button>
            </HStack>
            <Box maxH="58vh" overflowY="auto" borderWidth="1px" borderColor={borderColor} borderRadius="md">
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} bg={headerBg} zIndex={1}>
                  <Tr>
                    <Th w="36%">Key</Th>
                    <Th>Value</Th>
                    <Th w="48px" />
                  </Tr>
                </Thead>
                <Tbody>
                  {draftSpecs.map((r) => (
                    <Tr key={r.id}>
                      <Td verticalAlign="top">
                        <Input
                          size="sm"
                          value={r.key}
                          onChange={(e) => updateSpec(r.id, 'key', e.target.value)}
                          color={inputColor}
                        />
                      </Td>
                      <Td verticalAlign="top">
                        <Input
                          size="sm"
                          value={r.value}
                          onChange={(e) => updateSpec(r.id, 'value', e.target.value)}
                          color={inputColor}
                        />
                      </Td>
                      <Td>
                        <IconButton
                          size="sm"
                          aria-label="Remove row"
                          icon={<DeleteIcon />}
                          variant="ghost"
                          onClick={() => removeSpec(r.id)}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveSpecs} isLoading={saving}>
              Save &amp; update listings
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={kind === 'fitment'} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="94vw">
          <ModalHeader pr={14}>
            Edit catalog fitment — {row.canonicalSku}
            <Text as="span" fontSize="sm" fontWeight="normal" color={mutedTextColor} ml={2}>
              ({fitmentRowCount} row{fitmentRowCount === 1 ? '' : 's'})
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" borderRadius="md" mb={4}>
              <AlertIcon />
              Remove rows you do not want in the catalog. Saving replaces fitment on linked eBay listings
              (ReplaceAll). Adding or editing rows inline is not supported here — use listing enrichment to
              refresh from eBay.
            </Alert>
            <Box maxH="60vh" overflowY="auto" borderWidth="1px" borderColor={borderColor} borderRadius="md">
              <Table size="sm" variant="simple">
                <Thead position="sticky" top={0} bg={headerBg} zIndex={1}>
                  <Tr>
                    <Th>Year</Th>
                    <Th>Make</Th>
                    <Th>Model</Th>
                    <Th>Trim</Th>
                    <Th>Engine</Th>
                    <Th minW="160px">Notes</Th>
                    <Th w="48px" />
                  </Tr>
                </Thead>
                <Tbody>
                  {draftFitment.length === 0 ? (
                    <Tr>
                      <Td colSpan={7}>
                        <Text fontSize="sm" color={mutedTextColor}>
                          No fitment rows in catalog.
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    draftFitment.map((r) => (
                      <Tr key={r.id}>
                        {['Year', 'Make', 'Model', 'Trim', 'Engine', 'Notes'].map((field) => (
                          <Td key={field}>
                            <Text fontSize="sm" color={inputColor} noOfLines={field === 'Notes' ? 3 : 1}>
                              {r[field] || '—'}
                            </Text>
                          </Td>
                        ))}
                        <Td>
                          <IconButton
                            size="sm"
                            aria-label="Remove row"
                            icon={<DeleteIcon />}
                            variant="ghost"
                            onClick={() => removeFitment(r.id)}
                          />
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveFitment} isLoading={saving}>
              Save &amp; update listings
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ItemsCatalogAttributeModals;
